import React, { useEffect, useState } from "react";
import { FallbackProps, ErrorBoundary } from "react-error-boundary";
import { useRouteError, isRouteErrorResponse } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  RotateCcw,
  Home,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  RefreshCw,
} from "lucide-react";
import { reportClientError } from "@/api/reportClientError";

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg =
    error instanceof Error
      ? error.message
      : typeof error === "string"
      ? error
      : "";
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Failed to fetch") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    (error instanceof TypeError && msg.includes("fetch"))
  );
}

function getErrorMessage(error: unknown): string {
  if (!error) return "An unexpected error occurred.";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message || error.name || "An unexpected error occurred.";
  if (
    typeof error === "object" &&
    error !== null &&
    "statusText" in error &&
    typeof (error as { statusText?: unknown }).statusText === "string"
  ) {
    return (error as { statusText: string }).statusText;
  }
  if (
    typeof error === "object" &&
    error !== null &&
    "data" in error &&
    typeof (error as { data?: unknown }).data === "string"
  ) {
    return (error as { data: string }).data;
  }
  return "An unexpected error occurred.";
}

export function generateIncidentId(): string {
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  const time = Date.now().toString(36).substring(2, 6).toUpperCase();
  return `ERR-${rand}-${time}`;
}

export interface ErrorWithIncident extends Error {
  __pc_incident_id__?: string;
  __pc_reported__?: boolean;
}

export interface ErrorFallbackProps extends FallbackProps {
  standalone?: boolean;
}

export function ErrorFallback({
  error,
  resetErrorBoundary,
  standalone = true,
}: ErrorFallbackProps) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  const incidentId = React.useMemo(() => {
    const err = error as ErrorWithIncident | undefined;
    if (err?.__pc_incident_id__) {
      return err.__pc_incident_id__;
    }
    const newId = generateIncidentId();
    if (err) {
      err.__pc_incident_id__ = newId;
    }
    return newId;
  }, [error]);

  useEffect(() => {
    if (isChunkLoadError(error)) {
      const reloadKey = "error-boundary-chunk-reload";
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();

      // Auto-reload once to get fresh assets; prevent infinite loop with 10s guard
      if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
        sessionStorage.setItem(reloadKey, now.toString());
        window.location.reload();
      }
    }
  }, [error]);

  const reportedRef = React.useRef(false);

  useEffect(() => {
    if (!reportedRef.current && incidentId) {
      reportedRef.current = true;
      const errorName =
        error instanceof Error
          ? error.name
          : typeof error === "string"
          ? "StringError"
          : "UnknownError";
      const errorStack = error instanceof Error ? error.stack : undefined;
      const pageUrl =
        typeof window !== "undefined" ? window.location.href : "unknown";
      const userAgent =
        typeof navigator !== "undefined" ? navigator.userAgent : "unknown";

      reportClientError({
        referenceId: incidentId,
        errorName,
        errorMessage: getErrorMessage(error),
        errorStack,
        pageUrl,
        userAgent,
      });
    }
  }, [incidentId, error]);

  const isChunkError = isChunkLoadError(error);
  const errorMessage = getErrorMessage(error);
  const isDev = Boolean(import.meta.env.DEV);

  const errorDetails = React.useMemo(() => {
    try {
      const details = {
        referenceId: incidentId,
        url: typeof window !== "undefined" ? window.location.href : "unknown",
        timestamp: new Date().toISOString(),
        message: errorMessage,
        name: error instanceof Error ? error.name : typeof error,
        stack: error instanceof Error ? error.stack : undefined,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      };
      return JSON.stringify(details, null, 2);
    } catch {
      return String(error);
    }
  }, [error, incidentId, errorMessage]);

  const handleCopyDetails = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(errorDetails);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // Ignore clipboard write failures in restricted contexts
    }
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  const handleReload = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    }
    window.location.reload();
  };

  const handleGoBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
    } else {
      handleGoHome();
    }
  };

  return (
    <div
      className={`flex w-full flex-col items-center justify-center bg-gradient-to-b from-slate-50 via-slate-100/60 to-slate-100 p-4 sm:p-6 md:p-8 ${
        standalone ? "min-h-screen" : "min-h-[calc(100vh-3.5rem)] h-full"
      }`}
      role="alert"
    >
      <div className="w-full max-w-xl rounded-3xl border border-slate-200/80 bg-white p-6 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.07)] sm:p-10">
        <div className="flex flex-col items-center text-center">
          {/* Elegant Alert Badge */}
          <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl bg-amber-500/10 border border-amber-500/20 shadow-inner">
            <div className="absolute inset-0 rounded-2xl bg-amber-400/10 blur-xl"></div>
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-500 text-white shadow-md shadow-amber-500/30">
              <AlertTriangle className="h-6 w-6 stroke-[2.2]" />
            </div>
          </div>

          <h1 className="mt-6 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {isChunkError
              ? t("error_page_app_updated_title", "Application Updated")
              : t("error_page_title", "Something went wrong")}
          </h1>

          <p className="mt-3 text-sm leading-relaxed text-slate-500 sm:text-base max-w-md mx-auto">
            {isChunkError
              ? t(
                  "error_page_app_updated_description",
                  "A new version of the application is available. Please reload the page to continue."
                )
              : t(
                  "error_page_description",
                  "An unexpected error occurred. You can return to the dashboard or refresh the page."
                )}
          </p>

          {/* Reference ID Pill */}
          {!isChunkError && (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-mono font-medium text-slate-500">
              <span>{t("error_page_reference", "Reference ID")}:</span>
              <span className="font-semibold text-slate-700">{incidentId}</span>
            </div>
          )}
        </div>

        {/* Primary & Secondary Action Buttons */}
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-stretch sm:justify-center">
          <button
            type="button"
            onClick={handleGoHome}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-indigo-500/20 transition-all hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg hover:shadow-indigo-500/30 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 active:scale-[0.99] whitespace-nowrap"
          >
            <Home className="h-4 w-4 shrink-0" />
            <span>{t("error_page_dashboard_button", "Go to Dashboard")}</span>
          </button>

          <button
            type="button"
            onClick={handleReload}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 active:scale-[0.99] whitespace-nowrap"
          >
            <RotateCcw className="h-4 w-4 shrink-0 text-slate-500" />
            <span>{t("error_page_reload_button", "Reload Page")}</span>
          </button>
        </div>

        {/* Subtle Tertiary Back Link */}
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={handleGoBack}
            className="inline-flex items-center gap-1.5 py-1 text-xs font-medium text-slate-400 transition hover:text-slate-700 focus:outline-none"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>{t("error_page_back_button", "Go Back")}</span>
          </button>
        </div>

        {/* Collapsible Technical & Support Details */}
        {!isChunkError && (
          <div className="mt-6 border-t border-slate-100 pt-5">
            <button
              type="button"
              onClick={() => setShowDetails((prev) => !prev)}
              className="group flex w-full items-center justify-between rounded-xl bg-slate-50/80 px-4 py-2.5 text-xs font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 border border-slate-100"
            >
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-400 group-hover:bg-indigo-500 transition-colors"></span>
                {showDetails
                  ? t("error_page_hide_details", "Hide technical details")
                  : t("error_page_support_info", "Support & diagnostic info")}
              </span>
              {showDetails ? (
                <ChevronUp className="h-4 w-4 text-slate-400 group-hover:text-slate-700" />
              ) : (
                <ChevronDown className="h-4 w-4 text-slate-400 group-hover:text-slate-700" />
              )}
            </button>

            {showDetails && (
              <div className="mt-3 space-y-2.5 animate-in fade-in duration-200">
                <div className="flex items-center justify-between rounded-lg bg-slate-50 p-2.5 border border-slate-100 text-xs">
                  <div className="flex flex-col gap-0.5">
                    <span className="font-semibold text-slate-700">
                      {isDev ? errorMessage : `${t("error_page_reference", "Reference ID")}: ${incidentId}`}
                    </span>
                    {!isDev && (
                      <span className="text-[11px] text-slate-400">
                        {new Date().toLocaleString()}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyDetails}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                        <span className="text-emerald-600">
                          {t("error_page_copied", "Copied to clipboard")}
                        </span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5 text-slate-400" />
                        <span>{t("error_page_copy_details", "Copy details")}</span>
                      </>
                    )}
                  </button>
                </div>

                {isDev && (
                  <div className="max-h-52 overflow-y-auto rounded-xl bg-slate-950 p-3.5 font-mono text-[11px] text-slate-300 shadow-inner border border-slate-800">
                    <pre className="whitespace-pre-wrap break-all leading-relaxed text-slate-400">
                      {errorDetails}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function RouteErrorFallback({
  standalone = true,
}: {
  standalone?: boolean;
} = {}) {
  const routeError = useRouteError();

  let error: Error;
  if (isRouteErrorResponse(routeError)) {
    error = new Error(
      `${routeError.status} ${routeError.statusText}: ${
        typeof routeError.data === "string"
          ? routeError.data
          : JSON.stringify(routeError.data)
      }`
    );
  } else if (routeError instanceof Error) {
    error = routeError;
  } else if (typeof routeError === "string") {
    error = new Error(routeError);
  } else {
    error = new Error(
      (routeError as { statusText?: string })?.statusText ||
        "An unexpected routing error occurred."
    );
  }

  return (
    <ErrorFallback
      error={error}
      standalone={standalone}
      resetErrorBoundary={() => {
        window.location.reload();
      }}
    />
  );
}

export function SectionErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const incidentId = React.useMemo(() => {
    const err = error as ErrorWithIncident | undefined;
    if (err?.__pc_incident_id__) {
      return err.__pc_incident_id__;
    }
    const newId = generateIncidentId();
    if (err) {
      err.__pc_incident_id__ = newId;
    }
    return newId;
  }, [error]);

  const reportedRef = React.useRef(false);

  useEffect(() => {
    const err = error as ErrorWithIncident | undefined;
    if (err?.__pc_reported__) {
      return;
    }
    if (!reportedRef.current && incidentId) {
      reportedRef.current = true;
      if (err) {
        err.__pc_reported__ = true;
      }
      const errorName =
        error instanceof Error
          ? error.name
          : typeof error === "string"
          ? "StringError"
          : "UnknownError";
      const errorStack = error instanceof Error ? error.stack : undefined;
      const pageUrl =
        typeof window !== "undefined" ? window.location.href : "unknown";
      const userAgent =
        typeof navigator !== "undefined" ? navigator.userAgent : "unknown";

      reportClientError({
        referenceId: incidentId,
        errorName,
        errorMessage: getErrorMessage(error),
        errorStack,
        pageUrl,
        userAgent,
      });
    }
  }, [incidentId, error]);

  const handleCopyIncident = async () => {
    try {
      if (navigator.clipboard && incidentId) {
        await navigator.clipboard.writeText(incidentId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // Ignore clipboard write failures
    }
  };

  return (
    <div
      role="alert"
      className="my-4 flex flex-col items-center justify-center rounded-xl border border-rose-200 bg-rose-50/50 p-6 text-center"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 text-rose-600">
        <AlertTriangle className="h-5 w-5" />
      </div>
      <h3 className="mt-3 text-sm font-semibold text-rose-900">
        {t("error_page_section_title", "Unable to load this section")}
      </h3>
      <p className="mt-1 text-xs text-rose-700 max-w-md">
        {error?.message ||
          t(
            "error_page_section_description",
            "An error occurred while displaying this section. Please try again."
          )}
      </p>

      {incidentId && (
        <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-rose-100/80 px-2.5 py-0.5 text-[11px] font-mono font-medium text-rose-800 border border-rose-200">
          <span>{t("error_page_reference", "Reference ID")}: {incidentId}</span>
          <button
            type="button"
            onClick={handleCopyIncident}
            title={t("error_page_copy_incident", "Copy reference ID")}
            className="hover:text-rose-950 transition-colors ml-0.5"
          >
            {copied ? (
              <Check className="h-3 w-3 text-emerald-600" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </button>
        </div>
      )}

      {resetErrorBoundary && (
        <button
          type="button"
          onClick={resetErrorBoundary}
          className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-1"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {t("error_page_retry_button", "Try again")}
        </button>
      )}
    </div>
  );
}

interface SectionErrorBoundaryProps {
  children: React.ReactNode;
  onReset?: () => void;
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

export function SectionErrorBoundary({
  children,
  onReset,
  onError,
}: SectionErrorBoundaryProps) {
  const handleError = (error: Error, info: React.ErrorInfo) => {
    const err = error as ErrorWithIncident;
    if (!err.__pc_incident_id__) {
      err.__pc_incident_id__ = generateIncidentId();
    }

    if (!err.__pc_reported__) {
      err.__pc_reported__ = true;
      const pageUrl =
        typeof window !== "undefined" ? window.location.href : "unknown";
      const userAgent =
        typeof navigator !== "undefined" ? navigator.userAgent : "unknown";
      const fullStack = info?.componentStack
        ? `${error.stack || error.message}\n\nComponent Stack:\n${info.componentStack}`
        : error.stack;

      reportClientError({
        referenceId: err.__pc_incident_id__,
        errorName: error.name || "Error",
        errorMessage: getErrorMessage(error),
        errorStack: fullStack,
        pageUrl,
        userAgent,
        metadata: info?.componentStack
          ? { componentStack: info.componentStack }
          : undefined,
      });
    }

    onError?.(error, info);
  };

  return (
    <ErrorBoundary
      FallbackComponent={SectionErrorFallback}
      onReset={onReset}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
}